import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AttachFile,
  FormatBold,
  FormatItalic,
  Image as ImageIcon,
  Redo,
  Undo,
} from '@mui/icons-material';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { useTranslate } from 'react-admin';
import InsertImageDialog, { type InsertedImage } from './InsertImageDialog';
import AttachFileDialog from './AttachFileDialog';

const ImageWithCid = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      cid: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-cid'),
        renderHTML: (attrs) =>
          attrs.cid ? { 'data-cid': attrs.cid as string } : {},
      },
      width: {
        default: null,
        parseHTML: (el) => {
          const attr = el.getAttribute('width');
          if (attr) return Number(attr);
          const style = el.style.width;
          if (style) {
            const n = parseInt(style, 10);
            return Number.isFinite(n) ? n : null;
          }
          return null;
        },
        renderHTML: (attrs) =>
          attrs.width != null ? { width: String(attrs.width) } : {},
      },
      height: {
        default: null,
        parseHTML: (el) => {
          const attr = el.getAttribute('height');
          if (attr) return Number(attr);
          const style = el.style.height;
          if (style) {
            const n = parseInt(style, 10);
            return Number.isFinite(n) ? n : null;
          }
          return null;
        },
        renderHTML: (attrs) =>
          attrs.height != null ? { height: String(attrs.height) } : {},
      },
    };
  },
}).configure({
  inline: true,
  allowBase64: true, 
});

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  error = false,
  helperText,
  label,
  required = false,
}) => {
  const translate = useTranslate();
  const theme = useTheme();
  const rg = theme.custom.rg;
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [fileDialogOpen, setFileDialogOpen] = useState(false);

  // Roomgrid toolbar buttons: 30×28 ghost squares, teal-weak when active.
  const toolbarBtnSx = (active = false) => ({
    width: 30,
    height: 28,
    border: 'none',
    borderRadius: '6px',
    color: active ? rg.accent : rg.text2,
    backgroundColor: active ? rg.accentWeak : 'transparent',
    '&:hover': { backgroundColor: rg.bgElev },
    '&.Mui-disabled': { border: 'none', opacity: 0.45 },
    '& .MuiSvgIcon-root': { fontSize: 15 },
  });

  const editor = useEditor({
    extensions: [StarterKit, ImageWithCid],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const handleInsertImage = (image: InsertedImage): void => {
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'image',
        attrs: {
          src: image.src,
          alt: image.alt ?? image.cid ?? '',
          cid: image.cid ?? null,
          width: image.width ?? null,
          height: image.height ?? null,
        },
      })
      .run();
  };

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            display: 'block',
            mb: '5px',
            fontSize: '10.5px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 600,
            color: error ? rg.danger.line : rg.text3,
          }}
        >
          {label}
          {required && ' *'}
        </Typography>
      )}

      <Box
        sx={{
          border: `1px solid ${error ? rg.danger.line : rg.border}`,
          borderRadius: '9px',
          overflow: 'hidden',
        }}
      >
        {/* Toolbar — Roomgrid: sunken strip, ghost 30×28 buttons. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            p: '5px',
            borderBottom: `1px solid ${rg.border}`,
            backgroundColor: rg.bg2,
          }}
        >
          <Tooltip title={translate('resources.test.editor.bold')}>
            <IconButton
              onClick={() => editor.chain().focus().toggleBold().run()}
              sx={toolbarBtnSx(editor.isActive('bold'))}
            >
              <FormatBold />
            </IconButton>
          </Tooltip>
          <Tooltip title={translate('resources.test.editor.italic')}>
            <IconButton
              onClick={() => editor.chain().focus().toggleItalic().run()}
              sx={toolbarBtnSx(editor.isActive('italic'))}
            >
              <FormatItalic />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '4px' }} />

          <Tooltip title={translate('resources.test.editor.insertImage')}>
            <IconButton
              onClick={() => setImageDialogOpen(true)}
              sx={toolbarBtnSx()}
            >
              <ImageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={translate('resources.test.editor.attachFile')}>
            <IconButton
              onClick={() => setFileDialogOpen(true)}
              sx={toolbarBtnSx()}
            >
              <AttachFile />
            </IconButton>
          </Tooltip>

          <Box sx={{ flex: 1 }} />

          <Tooltip title={translate('resources.test.editor.undo')}>
            <span>
              <IconButton
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                sx={toolbarBtnSx()}
              >
                <Undo />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={translate('resources.test.editor.redo')}>
            <span>
              <IconButton
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                sx={toolbarBtnSx()}
              >
                <Redo />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Content area */}
        <Box
          sx={{
            minHeight: 160,
            maxHeight: 400,
            overflowY: 'auto',
            px: '16px',
            py: '12px',
            backgroundColor: rg.bgElev,
            fontSize: '13px',
            lineHeight: 1.6,
            '& .ProseMirror': {
              outline: 'none',
              minHeight: 140,
              '& p': { my: 0.5 },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 0.5,
              },
              '& ul, & ol': { pl: 3, my: 0.5 },
            },
            '& .ProseMirror p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              float: 'left',
              color: 'text.disabled',
              pointerEvents: 'none',
              height: 0,
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Box>

      {helperText && (
        <Typography
          sx={{
            display: 'block',
            mt: '4px',
            fontSize: '11px',
            color: error ? rg.danger.line : rg.text3,
          }}
        >
          {helperText}
        </Typography>
      )}

      <InsertImageDialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        onInsert={handleInsertImage}
      />
      <AttachFileDialog
        open={fileDialogOpen}
        onClose={() => setFileDialogOpen(false)}
      />
    </Box>
  );
};

export default RichTextEditor;
